import { useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Appointment, Customer, Treatment } from '@/lib/types';
import { Card, PageHeader, Pill, cellStyle, thStyle } from '@/components/ui';
import { Icon, I } from '@/components/icons';
import { dt, STAGE_LABEL } from '@/lib/format';

export function SearchPage() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const q = (params.get('q') ?? '').trim();

  const customers = useQuery({
    queryKey: ['search-customers', q],
    enabled: q.length > 0,
    queryFn: () => api.get<Customer[]>(`/customers?q=${encodeURIComponent(q)}`),
  });
  const treatments = useQuery({ queryKey: ['treatments'], queryFn: () => api.get<Treatment[]>('/catalog/treatments') });
  const appts = useQuery({ queryKey: ['appointments'], queryFn: () => api.get<Appointment[]>('/appointments') });

  const lc = q.toLowerCase();
  const custList = customers.data ?? [];
  const matchedCustIds = useMemo(() => new Set(custList.map((c) => c.id)), [custList]);

  const txList = (treatments.data ?? []).filter(
    (t) => q && (t.name.toLowerCase().includes(lc) || t.code.toLowerCase().includes(lc) || t.short.toLowerCase().includes(lc)),
  );
  const custName = (id: string) => custList.find((c) => c.id === id)?.name ?? id;
  const txName = (id: string) => treatments.data?.find((t) => t.id === id)?.name ?? id;

  const apptList = (appts.data ?? []).filter(
    (a) => q && (matchedCustIds.has(a.customerId) || (a.note ?? '').toLowerCase().includes(lc)),
  );

  const totalCount = custList.length + txList.length + apptList.length;

  if (!q) {
    return (
      <div>
        <PageHeader title="全域搜尋" subtitle="跨顧客 / 方案 / 預約" />
        <Card title="請輸入關鍵字"><div style={{ padding: 12, color: 'var(--ink-4)', fontSize: 13 }}>使用上方搜尋框輸入姓名 / 電話 / 方案名稱後按 Enter。</div></Card>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={`搜尋：「${q}」`} subtitle={`跨顧客 / 方案 / 預約 · 共 ${totalCount} 筆結果`} />

      <Card title={`顧客（${custList.length}）`} icon={<Icon d={I.user} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={thStyle}>姓名</th><th style={thStyle}>電話</th><th style={thStyle}>分級</th><th style={thStyle}>標籤</th></tr></thead>
          <tbody>
            {custList.map((c) => (
              <tr key={c.id} style={{ cursor: 'pointer' }} onClick={() => nav(`/customers?q=${encodeURIComponent(c.phone)}`)}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{c.name}</td>
                <td style={cellStyle} className="num">{c.phone}</td>
                <td style={cellStyle}>{c.tier}</td>
                <td style={cellStyle}>{c.tags.map((t) => <Pill key={t} bg="var(--surface-2)" color="var(--ink-2)" style={{ marginRight: 4 }}>{t}</Pill>)}</td>
              </tr>
            ))}
            {custList.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>無相符顧客</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title={`方案（${txList.length}）`} icon={<Icon d={I.package} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={thStyle}>名稱</th><th style={thStyle}>代碼</th><th style={thStyle}>分類</th><th style={{ ...thStyle, textAlign: 'right' }}>時長</th></tr></thead>
          <tbody>
            {txList.map((t) => (
              <tr key={t.id} style={{ cursor: 'pointer' }} onClick={() => nav('/catalog')}>
                <td style={{ ...cellStyle, fontWeight: 600 }}>{t.name}</td>
                <td style={cellStyle} className="num">{t.code}</td>
                <td style={cellStyle}>{t.category}</td>
                <td style={{ ...cellStyle, textAlign: 'right' }} className="num">{t.durationMin} 分</td>
              </tr>
            ))}
            {txList.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>無相符方案</td></tr>}
          </tbody>
        </table>
      </Card>

      <Card title={`預約（${apptList.length}）`} icon={<Icon d={I.cal} size={14} style={{ color: 'var(--primary)' }} />}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr><th style={thStyle}>時間</th><th style={thStyle}>顧客</th><th style={thStyle}>療程</th><th style={thStyle}>狀態</th></tr></thead>
          <tbody>
            {apptList.slice(0, 30).map((a) => (
              <tr key={a.id} style={{ cursor: 'pointer' }} onClick={() => nav('/booking')}>
                <td style={cellStyle} className="num">{dt(a.startAt)}</td>
                <td style={cellStyle}>{custName(a.customerId)}</td>
                <td style={cellStyle}>{txName(a.treatmentId)}</td>
                <td style={cellStyle}><Pill bg="var(--surface-2)" color="var(--ink-2)">{STAGE_LABEL[a.stage] ?? a.stage}</Pill></td>
              </tr>
            ))}
            {apptList.length === 0 && <tr><td style={{ ...cellStyle, color: 'var(--ink-4)' }} colSpan={4}>無相符預約</td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
